import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Quadratics — where factoring is spent.
 *
 * Four lessons, in the order the subject actually developed: solve by factoring
 * where you can, understand the curve that explains why there are two answers,
 * learn the rebuild that works when factoring will not, then meet the formula
 * that is that rebuild performed once and for all.
 *
 * `completing-the-square` sits third rather than last on purpose. Taught after
 * the formula it is an oddity — a slower way to get an answer you already have
 * a formula for. Taught before it, the formula stops being something to
 * memorise and becomes something the student has already done, once, with
 * numbers.
 */
export const QUADRATIC_LESSONS: Lesson[] = [
  lesson({
    id: 'quadratics-01',
    unitId: 'unit-quadratics',
    conceptId: 'quadratics',
    title: 'Solving Quadratic Equations',
    summary: 'Two solutions rather than one, and the zero-product rule that finds them.',
    objectives: [
      'Use the zero-product rule on a factorised equation',
      'Solve a quadratic by factorising first',
      'Solve x² = k, keeping both signs',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Why two answers?',
        blocks: [
          text('Every equation you have solved so far had one answer. This one has two:'),
          math('x^{2} = 25 \\quad\\Longrightarrow\\quad x = 5 \\text{ or } x = -5'),
          text('Both are genuinely correct, because both square to 25. A **quadratic** equation — one containing x² — normally has two solutions, and finding only one is only half an answer.'),
        ],
      },
      intuition: {
        title: 'The zero-product rule',
        blocks: [
          text('Here is the idea the whole topic rests on, and it is almost too simple to state.'),
          insight('If two numbers multiply to give **zero**, then at least one of them must be zero. Nothing else can produce a zero product.'),
          text('That is why factoring was worth learning. Given a quadratic set equal to zero, factorise it and the rule reads off the answers:'),
          math('x^{2} + 5x + 6 = 0 \\;\\Longrightarrow\\; (x + 2)(x + 3) = 0 \\;\\Longrightarrow\\; x = -2 \\text{ or } x = -3'),
          warning('The rule works **only against zero**. From (x + 2)(x + 3) = 12 you cannot conclude that x + 2 = 12 or x + 3 = 12 — a product of 12 can be made in endless ways. Rearrange to make one side zero before factorising.', 'Only zero will do'),
          text('So the method has a fixed shape:'),
          list([
            'Rearrange so that one side is zero.',
            'Factorise the other side.',
            'Set each factor to zero in turn.',
            'Solve each of those small equations.',
          ], true),
          text('The other route, when there is no x term, is to take roots directly — remembering that two numbers square to any positive value.'),
          math('(x - 3)^{2} = 16 \\;\\Longrightarrow\\; x - 3 = \\pm 4 \\;\\Longrightarrow\\; x = 7 \\text{ or } x = -1'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('The roots are where the sign of each factor changes, and the product is zero exactly at those two points:'),
          figure(
            { kind: 'number-line', from: -6, to: 2, step: 1, marks: [{ at: -3, label: 'x = −3' }, { at: -2, label: 'x = −2' }] },
            'The two solutions of x² + 5x + 6 = 0. Between them the product is negative; outside them it is positive.',
          ),
          table(['Equation', 'Factorised', 'Solutions'], [
            ['x² − 9 = 0', '(x + 3)(x − 3)', '3 and −3'],
            ['x² + 7x + 12 = 0', '(x + 3)(x + 4)', '−3 and −4'],
            ['x² − 5x = 0', 'x(x − 5)', '0 and 5'],
            ['x² + 6x + 9 = 0', '(x + 3)²', '−3 only'],
          ]),
          insight('The third row is worth noticing: x is itself a factor, so x = 0 is a solution. Dividing both sides by x would have destroyed it — never divide an equation by something that might be zero.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve x² + 3x = 10.'),
          steps([
            { label: 'Rearrange so one side is zero. The rule needs it.', latex: 'x^{2} + 3x - 10 = 0' },
            { label: 'Factorise: two numbers multiplying to −10 and adding to 3.', latex: '(x + 5)(x - 2) = 0' },
            { label: 'Set each factor to zero.', latex: 'x + 5 = 0 \\text{ or } x - 2 = 0' },
            { label: 'Solve each.', latex: 'x = -5 \\text{ or } x = 2' },
            { label: 'Check both in the original.', latex: '25 - 15 = 10 \\;\\checkmark \\quad 4 + 6 = 10 \\;\\checkmark' },
          ]),
          tip('Check both solutions, not just the one that looks nicer. A sign error usually breaks exactly one of them, so checking only one catches half the mistakes.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('quadratics', 4)] },
      independent: { title: 'On your own', blocks: [practice('quadratics', 6)] },
      challenge: { title: 'Something harder', blocks: [practice('quadratics', 3, 'Equations needing rearrangement before the rule applies.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A quadratic normally has two solutions.',
            'If a product is zero, one of its factors is zero — and this works only against zero.',
            'Rearrange to = 0, factorise, then set each factor to zero.',
            'x² = k gives x = ±√k, both signs.',
            'Never divide an equation by something that could be zero.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'quadratic-graphs-01',
    unitId: 'unit-quadratics',
    conceptId: 'quadratic-graphs',
    title: 'Quadratic Functions and Parabolas',
    summary: 'The curve behind the equation — where its lowest point is, and where it crosses.',
    objectives: [
      'Describe a parabola: direction, vertex and axis of symmetry',
      'Read the vertex from completed-square form',
      'Connect the roots of an equation to the x-intercepts of its graph',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'The shape behind the answers',
        blocks: [
          text('Every quadratic function draws the same shape: a **parabola**. Throw a ball and its path is one; the cable of a suspension bridge hangs in one; a satellite dish is one rotated.'),
          math('y = x^{2} - 4'),
          text('And the two solutions from the last lesson stop being an algebraic curiosity. They are the two places the curve crosses the x-axis.'),
        ],
      },
      intuition: {
        title: 'Three things to read off',
        blocks: [
          text('**Which way it opens** is decided by the sign of the x² coefficient, and by nothing else.'),
          list([
            'Positive: opens upwards, and the vertex is the lowest point.',
            'Negative: opens downwards, and the vertex is the highest point.',
          ]),
          text('**The vertex** is the turning point, and completed-square form hands it to you:'),
          math('y = (x - h)^{2} + k \\quad\\text{has its vertex at } (h,\\ k)'),
          insight('Why: a square is never negative, so (x − h)² is smallest when it is zero, which happens at x = h. There y = k. The turning point is where the squared bracket vanishes.'),
          warning('The sign flips. y = (x − 3)² + 2 has its vertex at x = **3**, not −3, because 3 is what makes the bracket zero. Reading the sign straight off the bracket is the standard error here.', 'The sign inside the bracket'),
          text('**The roots** are where y = 0 — the x-intercepts. This connects the two lessons: solving x² − 4 = 0 gives x = ±2, and the graph of y = x² − 4 crosses at −2 and 2. The same two numbers, arrived at two ways.'),
          insight('It also explains how many solutions to expect. A parabola can cross the axis twice, touch it once, or miss it entirely — which is exactly two, one or no real solutions.'),
          text('And parabolas are symmetric. The vertical line through the vertex is the **axis of symmetry**, so the two roots always sit at equal distances either side of it.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -8,
              max: 8,
              points: [
                { x: -2, y: 0, label: 'root' },
                { x: 2, y: 0, label: 'root' },
                { x: 0, y: -4, label: 'vertex' },
              ],
            },
            'y = x² − 4. The two roots at ±2, the vertex at (0, −4), and the y-axis as the axis of symmetry — with each root exactly 2 away from it.',
          ),
          table(['Discriminant', 'The curve', 'Real solutions'], [
            ['positive', 'crosses the axis twice', '2'],
            ['zero', 'touches the axis once', '1'],
            ['negative', 'never reaches the axis', '0'],
          ]),
          tip('If a quadratic has no real solutions, that is not a failure of your algebra. The graph simply never reaches the axis, and the next lesson gives you a way to know in advance.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('For y = (x − 1)² − 9, find the vertex, say which way it opens, and find the roots.'),
          steps([
            { label: 'The bracket is zero at x = 1, giving the vertex.', latex: '(1,\\ -9)' },
            { label: 'The x² coefficient is positive, so it opens upwards.', latex: '\\text{vertex is the minimum}' },
            { label: 'For the roots, set y = 0.', latex: '(x - 1)^{2} = 9' },
            { label: 'Take roots, both signs.', latex: 'x - 1 = \\pm 3' },
            { label: 'Solve.', latex: 'x = 4 \\text{ or } x = -2' },
          ]),
          text('Check the symmetry: the roots are 4 and −2, whose midpoint is 1 — the x-coordinate of the vertex, exactly as it must be. That is a free check on every quadratic you ever solve.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('quadratic-graphs', 4)] },
      independent: { title: 'On your own', blocks: [practice('quadratic-graphs', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('quadratic-graphs', 3, 'Vertices, and which way a curve opens.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Every quadratic graphs as a parabola.',
            'The sign of the x² coefficient decides which way it opens, and nothing else does.',
            'In y = (x − h)² + k the vertex is (h, k) — note the sign change.',
            'The roots are the x-intercepts, so there can be two, one or none.',
            'The roots are symmetric about the vertex, which is a free check.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'completing-the-square-01',
    unitId: 'unit-quadratics',
    conceptId: 'completing-the-square',
    title: 'Completing the Square',
    summary: 'Rebuilding a quadratic around a perfect square, which is where both the vertex and the formula come from.',
    objectives: [
      'Complete the square on a monic quadratic',
      'Read the vertex off the result',
      'Solve a quadratic that does not factorise',
      'Explain why a coefficient in front of x² must be factored out first',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Where did completed-square form come from?',
        blocks: [
          text('The last lesson read the vertex straight off y = (x − 3)² − 4 and left one question hanging: where does that form come from, when the quadratic you are handed looks like this?'),
          math('y = x^{2} - 6x + 5'),
          text('The two are the same function — expand the first and you get the second. **Completing the square** is the procedure that runs that backwards, and it is the one technique in the topic that never fails.'),
          insight('Factoring only works when the roots happen to be tidy. Completing the square works always, which is why the quadratic formula is nothing more than this procedure carried out once, on ax² + bx + c, with letters instead of numbers.'),
        ],
      },
      intuition: {
        title: 'Halve it, square it, take it back off',
        blocks: [
          text('Start from what a squared bracket expands to, because the procedure is that identity read from right to left:'),
          math('(x + p)^{2} = x^{2} + 2px + p^{2}'),
          text('The middle coefficient is **twice** p, and the constant is p **squared**. So given x² + 6x, the p that would produce that middle term is 3 — half of 6 — and the bracket (x + 3)² carries a spare 9 with it.'),
          math('x^{2} + 6x = (x + 3)^{2} - 9'),
          insight('Halve the x-coefficient to find what goes *inside* the bracket. Square that to find what has to come back *off* outside. Those are two different numbers, and each has exactly one job.'),
          warning('The subtraction is not optional. (x + 3)² is x² + 6x + 9, so writing x² + 6x as (x + 3)² adds a 9 that was never there. Rewriting an expression may change its shape and must never change its value.', 'Add nothing without removing it'),
          text('With a constant already present, the two constants simply combine:'),
          math('x^{2} - 6x + 5 = (x - 3)^{2} - 9 + 5 = (x - 3)^{2} - 4'),
          text('And now the vertex is readable — (3, −4) — which is the whole reason for doing it.'),
          warning('Halving the coefficient only works when x² stands alone. For 2x² + 12x + 5, factor the 2 out of the first two terms first: 2(x² + 6x) + 5. Complete the square inside the bracket, then multiply back out.', 'A coefficient in front changes the arithmetic'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('The name is literal. x² + 6x is a square of side x with two 3-by-x strips attached, and the two strips do not close the corner:'),
          figure(
            { kind: 'area-model', rows: 7, columns: 7, shadedRows: 4, shadedColumns: 4 },
            'x² + 6x as an area, drawn with x = 4. The dark square is x², the two pale strips are 3x each — and the empty corner is the 3 × 3 = 9 that would complete the big square of side x + 3. Adding that 9 makes the whole figure (x + 3)², which is exactly why it has to be subtracted straight back off.',
          ),
          table(['Expression', 'Half the x-coefficient', 'Completed square'], [
            ['x² + 8x', '4', '(x + 4)² − 16'],
            ['x² − 10x', '−5', '(x − 5)² − 25'],
            ['x² + 4x + 7', '2', '(x + 2)² + 3'],
            ['x² − 2x − 6', '−1', '(x − 1)² − 7'],
          ]),
          tip('Every one of these can be checked in ten seconds by expanding. Completing the square is unusual in having a check that is both trivial and complete.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve x² + 4x − 3 = 0 by completing the square.'),
          steps([
            { label: 'Half of 4 is 2, so the bracket is (x + 2)², which carries a spare 4.', latex: 'x^{2} + 4x = (x + 2)^{2} - 4' },
            { label: 'Rewrite the whole equation.', latex: '(x + 2)^{2} - 4 - 3 = 0' },
            { label: 'Combine the constants and move them across.', latex: '(x + 2)^{2} = 7' },
            { label: 'Take the root of both sides — both signs.', latex: 'x + 2 = \\pm\\sqrt{7}' },
            { label: 'Solve for x.', latex: 'x = -2 \\pm \\sqrt{7}' },
          ]),
          text('Those are the exact solutions, roughly 0.65 and −4.65. No search for factors could have found them, because 7 is not a perfect square — and this is precisely the case the next lesson turns into a formula.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('completing-the-square', 4)] },
      independent: { title: 'On your own', blocks: [practice('completing-the-square', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('completing-the-square', 3, 'Quadratics carrying a coefficient in front of the x².')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Halve the x-coefficient for the inside of the bracket; square it for what comes back off.',
            'Anything added has to be subtracted again — the value never changes.',
            'x² + bx + c becomes (x + b/2)² + (c − b²/4).',
            'The vertex of y = (x − h)² + k is (h, k), so completing the square finds it.',
            'Factor out any coefficient of x² before you start.',
            'It never fails, which is why the quadratic formula is this procedure done once in general.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'quadratic-formula-01',
    unitId: 'unit-quadratics',
    conceptId: 'quadratic-formula',
    title: 'The Quadratic Formula',
    summary: 'The solution to every quadratic there is — and a way to know how many there are before solving.',
    objectives: [
      'Apply the quadratic formula, identifying a, b and c correctly',
      'Use the discriminant to count the real solutions',
      'Choose sensibly between factorising and the formula',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'When factoring runs out',
        blocks: [
          text('Try to factorise this: x² + 4x + 1 = 0. Two numbers multiplying to 1 and adding to 4 — there are none among the whole numbers, and yet the graph plainly crosses the axis twice.'),
          text('Factoring is a search, and searches fail. This does not:'),
          math('x = \\frac{-b \\pm \\sqrt{b^{2} - 4ac}}{2a}'),
          text('The **quadratic formula** solves every quadratic equation that exists, whatever its coefficients. It is one of the very few formulas worth memorising outright, because there is no case it does not cover.'),
        ],
      },
      intuition: {
        title: 'Reading the formula',
        blocks: [
          text('Written for ax² + bx + c = 0, so the first job is identifying a, b and c — *with their signs*.'),
          warning('In x² − 5x + 6, b is −5, not 5. Dropping a minus sign here is the most common way the formula goes wrong, and the answer it produces looks perfectly plausible.', 'Signs belong to the coefficients'),
          text('The ± is where the two solutions come from — one taking the plus, one the minus. And the whole numerator sits over 2a:'),
          math('x = \\frac{-b \\pm \\sqrt{\\ldots}}{2a}, \\quad \\text{not} \\quad -b \\pm \\frac{\\sqrt{\\ldots}}{2a}'),
          text('The part under the root has a name and a job of its own. The **discriminant**:'),
          math('\\Delta = b^{2} - 4ac'),
          insight('It tells you how many solutions there are before you find any. Positive: the root is a real number and ± gives two answers. Zero: ± adds and subtracts nothing, so there is one. Negative: no real square root exists, so there are no real solutions — which is the algebra agreeing with a parabola that misses the axis.'),
          tip('Try factorising first anyway. When it works it is faster and less error-prone; when it does not, you have lost fifteen seconds and the formula is waiting.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          table(['Equation', 'a, b, c', 'Δ', 'Solutions'], [
            ['x² − 5x + 6 = 0', '1, −5, 6', '1', '2 and 3'],
            ['x² + 4x + 1 = 0', '1, 4, 1', '12', '−2 ± √3'],
            ['x² − 6x + 9 = 0', '1, −6, 9', '0', '3 only'],
            ['x² + x + 5 = 0', '1, 1, 5', '−19', 'none real'],
          ]),
          insight('The second row is why the formula exists. Its solutions are −2 + √3 and −2 − √3 — perfectly real, entirely correct, and unreachable by any search for whole-number factors.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve 2x² − 7x + 3 = 0.'),
          steps([
            { label: 'Identify the coefficients, signs included.', latex: 'a = 2,\\ b = -7,\\ c = 3' },
            { label: 'Compute the discriminant.', latex: '(-7)^{2} - 4(2)(3) = 49 - 24 = 25' },
            { label: 'Positive, so expect two solutions.', latex: '\\sqrt{25} = 5' },
            { label: 'Substitute, keeping the whole numerator over 2a.', latex: 'x = \\frac{7 \\pm 5}{4}' },
            { label: 'Take the plus.', latex: 'x = \\frac{12}{4} = 3' },
            { label: 'Take the minus.', latex: 'x = \\frac{2}{4} = \\tfrac{1}{2}' },
          ]),
          text('Note that −b became +7, because b was negative. And the discriminant was a perfect square, which means this one *could* have been factorised as (2x − 1)(x − 3) — a useful signal in both directions.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('quadratic-formula', 4)] },
      independent: { title: 'On your own', blocks: [practice('quadratic-formula', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('quadratic-formula', 3, 'Counting solutions from the discriminant alone.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The formula solves every quadratic, without exception.',
            'Identify a, b and c with their signs — a negative b is where it usually goes wrong.',
            'The whole numerator is divided by 2a.',
            'Δ = b² − 4ac gives the number of real solutions: two, one or none.',
            'Try factorising first; reach for the formula when it does not work.',
          ]),
        ],
      },
    },
  }),
];
